import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import ButtonBase from "@mui/material/ButtonBase";
import Checkbox from "@mui/material/Checkbox";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AssetImage from "./AssetImage";
import { getCustomDescriptionValue } from "../../../utils/asset";

const styles = {
  selected: {
    backgroundColor: "rgba(25, 118, 210, 0.08)",
    borderBottom: "1px solid #E0E0E0",
  },
  root: {
    borderBottom: "1px solid #E0E0E0",
  },
  groupedAssetHover: {
    borderBottom: "1px solid #E0E0E0",
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.02)",
    },
  },
};

export default function Item({
  value,
  info,
  selected,
  onClick,
  project,
  actions,
  clickableActions = false,
  isGroupedAsset = false,
  titleLines = 2,
  descriptionLines = 3,
  reserveActionsSpace = false,
  wrapDescription = false,
  stackOnMobile = true,
  selectable = false,
  checked = false,
}) {
  const { asset, quantity, attachments = [] } = value;
  const { itemId, itemCode, barcode, description, skuNumber, images = [] } = asset;
  const assetImage = images.find(img => img.default) || images[0];
  const imageUrl = attachments[0]?.url || assetImage?.url;

  const title = [itemId, itemCode, barcode, skuNumber].filter(Boolean).join(" | ");

  // Fallback to custom description field if standard description is empty
  const getDisplayDescription = () => {
    if (description) return description;
    return getCustomDescriptionValue(asset);
  };

  const displayDescription = getDisplayDescription();

  const content = (
    <Box
      display="flex"
      my={1.5}
      width="100%"
      overflow="hidden"
      alignItems={selectable ? "center" : undefined}
    >
      {selectable && (
        <Box mr={1} display="flex" alignItems="center">
          <Checkbox
            checked={checked}
            readOnly
            tabIndex={-1}
            inputProps={{ "aria-hidden": true }}
            size="small"
            sx={{ p: 0.5 }}
            disableRipple
          />
        </Box>
      )}
      <Box mr={3}>
        <AssetImage width={120} height={100} image={imageUrl} />
      </Box>
      <Box
        display="flex"
        justifyContent="space-between"
        width="100%"
        gap={1}
        flexDirection={stackOnMobile ? { xs: "column", sm: "row" } : "row"}
      >
        <Box
          display={isGroupedAsset ? "flex" : "block"}
          alignItems={isGroupedAsset ? "center" : "flex-start"}
          minHeight={isGroupedAsset ? "100px" : "auto"}
          flex={1}
          minWidth={0}
        >
          {!isGroupedAsset && (
            <>
              <Typography
                fontWeight={500}
                sx={{
                  display: "-webkit-box",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: titleLines,
                }}
              >
                {title}
              </Typography>
              <Typography
                mt={0.5}
                sx={
                  wrapDescription
                    ? {
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                      }
                    : {
                        display: "-webkit-box",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        WebkitBoxOrient: "vertical",
                        WebkitLineClamp: descriptionLines,
                      }
                }
              >
                {displayDescription}
              </Typography>
            </>
          )}
          {info ? (
            <Box mt={isGroupedAsset ? 0 : 0.5}>{info}</Box>
          ) : (
            <Typography mt={0.5}>
              <Box component="span" fontWeight={500}>
                Quantity:
              </Box>{" "}
              {quantity}
            </Typography>
          )}
        </Box>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="flex-end"
          flexShrink={0}
          minWidth={reserveActionsSpace && actions ? 40 : undefined}
          onMouseDown={clickableActions ? undefined : e => e.stopPropagation()}
          onClick={clickableActions ? undefined : e => e.stopPropagation()}
        >
          {actions}
          {isGroupedAsset && onClick && (
            <ChevronRightIcon sx={{ color: "text.secondary", ml: 1 }} />
          )}
        </Box>
      </Box>
    </Box>
  );

  const renderContent = () => {
    if (!onClick) {
      return <Box sx={{ width: "100%" }}>{content}</Box>;
    }

    if (selectable) {
      return (
        <Box
          role="checkbox"
          aria-checked={checked}
          tabIndex={0}
          onClick={onClick}
          onKeyDown={event => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              onClick(event);
            }
          }}
          sx={{
            width: "100%",
            cursor: "pointer",
            "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
            "&:focus-visible": {
              outline: "3px solid",
              outlineColor: "primary.main",
              outlineOffset: -3,
            },
          }}
        >
          {content}
        </Box>
      );
    }

    return (
      <ButtonBase
        sx={{ width: "100%", justifyContent: "space-between", textAlign: "left" }}
        onClick={onClick}
      >
        {content}
      </ButtonBase>
    );
  };

  return (
    <Box sx={selected ? styles.selected : (isGroupedAsset && onClick ? styles.groupedAssetHover : styles.root)}>
      {renderContent()}
    </Box>
  );
}
